import { useEffect, useMemo, useRef, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { ArrowLeft, ChevronRight, Lock, Plus } from 'lucide-react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useI18n } from '@/contexts/I18nContext';
import { detectCountryFromTimezone } from '@/utils/countryDetection';
import { useSavedCard } from '../hooks/useSavedCard';
import { usePremiumPrice } from '../hooks/usePremiumPrice';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import { useCreateSubscription } from '../hooks/useCreateSubscription';
import { useToast } from '@/shared/hooks/use-toast';
import { emit } from '@/integrations/analytics/emit';
import {
  PAYMENT_FAILED_CLIENT,
  SUBSCRIPTION_CHECKOUT_STARTED,
} from '@/integrations/analytics/events';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

function detectCountryCode(): string {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  return detectCountryFromTimezone(timezone) === 'BR' ? 'BR' : 'US';
}

// ── Brand pill ────────────────────────────────────────────────────────────────

const BRAND_GRADIENTS: Record<string, string> = {
  visa:       'from-[#1A1F71] to-[#1A1F71]',
  mastercard: 'from-[#EB001B] to-[#F79E1B]',
  amex:       'from-[#007BC1] to-[#007BC1]',
  discover:   'from-[#FF6000] to-[#FF6000]',
};

function BrandPill({ brand }: { brand: string }) {
  const gradient = BRAND_GRADIENTS[brand.toLowerCase()] ?? 'from-white/20 to-white/10';
  return (
    <span
      className={`inline-flex items-center justify-center bg-gradient-to-r ${gradient} rounded-[4px] px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wider text-white`}
    >
      {brand.toUpperCase()}
    </span>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

function ordinalDay(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
}

export default function SubscriptionCheckoutPlaceholderPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { t } = useI18n();
  const { card, isLoading: cardLoading } = useSavedCard();
  const { displayAmount, isLoading: priceLoading } = usePremiumPrice();
  const { isPro, subscription } = useSubscriptionStatus();
  const createSubscription = useCreateSubscription();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cardDeclined, setCardDeclined] = useState(false);
  const country = useMemo(() => detectCountryCode(), []);

  // Checkout impression. The subscriptions row is only created on success at
  // stripe-create-subscription, so this gap is PostHog-owned. Guarded so the
  // event fires once per mount (not per dependency re-render).
  const checkoutStartedRef = useRef(false);
  useEffect(() => {
    if (checkoutStartedRef.current || isPro) return;
    checkoutStartedRef.current = true;
    const origin = (location.state as { origin?: string } | null)?.origin ?? 'plan_page';
    emit(SUBSCRIPTION_CHECKOUT_STARTED, { plan: 'pro', origin });
  }, [isPro, location.state]);

  if (isPro) return <Navigate to="/settings/subscription" replace />;

  const anchorDay = (subscription as any)?.billing_cycle_anchor ?? new Date().getDate();
  const renewsHint = t('premium.checkout.renews_hint', { day: ordinalDay(anchorDay) });

  const isLoading = isSubmitting || createSubscription.isPending;
  const amount    = priceLoading ? '...' : (displayAmount ?? '--');

  const handleSubscribe = async () => {
    if (!card?.id) {
      toast({
        title:       t('premium.checkout.card_required_title'),
        description: t('premium.checkout.card_required_body'),
        variant:     'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await createSubscription.mutateAsync({
        country,
        paymentMethodId: card.id,
      });

      // Handle 3DS / requires_confirmation
      const needsConfirmation =
        result.payment_intent_status === 'requires_action' ||
        result.payment_intent_status === 'requires_confirmation';

      if (needsConfirmation && result.payment_intent_client_secret) {
        const stripe = await stripePromise;
        if (!stripe) throw new Error('Stripe failed to initialize');

        const confirmation = await stripe.confirmCardPayment(
          result.payment_intent_client_secret,
          { payment_method: card.id },
        );

        if (confirmation.error) {
          throw new Error(confirmation.error.message || 'Payment confirmation failed');
        }
      }

      navigate('/subscription/success', { replace: true });
    } catch (error) {
      const code = (error as { code?: unknown })?.code;
      const isCardDeclined = code === 'card_declined';
      // Pre-row failure: createSubscription rejected before the subscriptions
      // row was created, so this gap is PostHog-owned. Successful subscribes
      // and post-row dunning are DB-derivable.
      emit(PAYMENT_FAILED_CLIENT, {
        reason: typeof code === 'string' ? code : 'unexpected',
        service_type: 'subscription',
      });
      if (isCardDeclined) {
        setCardDeclined(true);
      } else {
        toast({
          title:       t('premium.checkout.error_title'),
          description: error instanceof Error ? error.message : t('premium.checkout.error_body'),
          variant:     'destructive',
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#160C0C] text-white">

      {/* ── Fixed header ── */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-[#160C0C] border-b border-white/[0.06]">
        <div className="max-w-md mx-auto w-full px-4 py-4 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Back"
            className="text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft size={18} />
          </button>
          <h1 className="text-base font-semibold">{t('premium.checkout.title')}</h1>
        </div>
      </div>

      {/* ── Scrollable content (pt-[76px] = header ~56px + original py-5 20px) ── */}
      <div>
        <div className="max-w-md mx-auto px-4 pt-[76px] pb-36 space-y-5">

          {/* RESUMO */}
          <div>
            <p className="text-[10px] font-bold tracking-[1.5px] uppercase text-white/40 mb-2.5">
              {t('premium.checkout.summary_label')}
            </p>

            <div className="rounded-[14px] border border-white/[0.06] bg-[#231a1a] overflow-hidden">
              {/* Plan header */}
              <div className="flex items-center gap-3 px-4 py-3.5 border-b border-white/[0.06]">
                <div className="shrink-0 w-[42px] h-[42px] rounded-[11px] bg-[#d72e16] flex items-center justify-center">
                  <span className="text-[11px] font-extrabold tracking-wider text-white">PRO</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-semibold text-white leading-snug">
                    {t('premium.checkout.plan_title')}
                  </p>
                  <p className="text-[11px] text-white/40 mt-0.5">
                    {renewsHint}
                  </p>
                </div>
              </div>

              {/* Line item */}
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-[13px] text-white/70">
                  {t('premium.checkout.plan_line_item')}
                </span>
                <span className="text-[13px] text-white">{amount}</span>
              </div>

              {/* Total */}
              <div className="flex items-center justify-between px-4 py-3 border-t border-white/[0.06]">
                <span className="text-[14px] font-bold text-white">
                  {t('premium.checkout.total_label')}
                </span>
                <span className="text-[14px] font-bold text-white">{amount}</span>
              </div>
            </div>
          </div>

          {/* MÉTODO DE PAGAMENTO */}
          <div>
            <p className="text-[10px] font-bold tracking-[1.5px] uppercase text-white/40 mb-2.5">
              {t('premium.checkout.payment_method_label')}
            </p>

            <div className="space-y-2">
              {/* Loading */}
              {cardLoading && (
                <div className="flex items-center gap-2 px-4 py-3.5 rounded-[14px] border border-white/[0.06] bg-[#231a1a] text-[13px] text-white/40">
                  {t('premium.checkout.loading_card')}
                </div>
              )}

              {/* Saved card (selected) */}
              {!cardLoading && card && (
                <div className="flex items-center gap-3 px-4 py-3.5 rounded-[14px] border border-[#d72e16] bg-[rgba(215,46,22,0.10)]">
                  <BrandPill brand={card.brand} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-white font-mono tracking-wider">
                      •••• •••• •••• {card.last4}
                    </p>
                    <p className="text-[11px] text-white/50 mt-0.5">
                      {t('premium.checkout.expires')} {card.exp_month}/{card.exp_year}
                    </p>
                  </div>
                  {/* Radio indicator */}
                  <div className="shrink-0 w-4 h-4 rounded-full border-2 border-[#d72e16] flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-[#d72e16]" />
                  </div>
                </div>
              )}

              {/* No card warning */}
              {!cardLoading && !card && (
                <p data-testid="no-card-warning" className="px-4 py-3 text-sm text-yellow-300 rounded-[14px] border border-white/[0.06] bg-[#231a1a]">
                  {t('premium.checkout.no_card')}
                </p>
              )}

              {/* Add new card */}
              <button
                type="button"
                onClick={() => navigate('/settings/payment-methods')}
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-[14px] border border-dashed border-white/[0.15] bg-[#231a1a] hover:bg-white/[0.03] transition-colors"
              >
                <div className="shrink-0 w-8 h-8 rounded-full border border-dashed border-white/20 flex items-center justify-center text-white/40">
                  <Plus size={14} />
                </div>
                <span className="flex-1 text-left text-[13px] text-white/60">
                  {t('premium.checkout.add_card')}
                </span>
                <ChevronRight size={14} className="text-white/30" />
              </button>
            </div>
          </div>

          {/* Save card toggle (locked — subscription requires saved card) */}
          <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-[14px] border border-white/[0.06] bg-[#231a1a] opacity-80">
            <div className="flex-1 min-w-0">
              <p className="text-[13px] text-white/85">
                {t('premium.checkout.save_card_label')}
              </p>
              <p className="text-[11px] text-white/40 mt-0.5 flex items-center gap-1">
                <Lock size={10} className="shrink-0" />
                {t('premium.checkout.save_card_hint')}
              </p>
            </div>
            <div
              role="switch"
              aria-checked="true"
              aria-disabled="true"
              className="shrink-0 w-9 h-5 rounded-full bg-sossiety-red flex items-center justify-end px-0.5 cursor-not-allowed"
            >
              <div className="w-4 h-4 rounded-full bg-white" />
            </div>
          </div>

          {/* Card declined error */}
          {cardDeclined && (
            <p className="px-4 py-3 text-sm text-red-300 rounded-[14px] border border-red-500/30 bg-red-500/10">
              {t('premium.checkout.card_declined')}
            </p>
          )}

          {/* Security note */}
          <div className="flex items-start gap-2.5 px-1">
            <Lock size={13} className="mt-0.5 shrink-0 text-white/30" />
            <p className="text-[11px] text-white/40 leading-relaxed">
              {t('premium.checkout.security_note')}{' '}
              {t('premium.checkout.cancel_note')}
            </p>
          </div>
        </div>
      </div>

      {/* ── Sticky bottom CTA ── */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#160c0c] border-t border-white/[0.06] px-4 pb-6 pt-4">
        <div className="max-w-md mx-auto space-y-3">
          <button
            type="button"
            data-testid="subscribe-cta-button"
            onClick={handleSubscribe}
            disabled={!card || isLoading}
            className="w-full h-[50px] rounded-full bg-[#d72e16] text-white text-[15px] font-semibold hover:bg-[#c02612] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading
              ? t('premium.checkout.subscribing')
              : `${t('premium.checkout.cta_pay')} ${amount}`}
          </button>

          <p className="text-[10px] text-white/30 text-center leading-relaxed">
            {t('premium.checkout.terms_prefix')}{' '}
            <button type="button" className="text-[#d72e16] underline underline-offset-2">
              {t('premium.checkout.terms_link')}
            </button>
            {t('premium.checkout.terms_suffix')}
          </p>
        </div>
      </div>
    </div>
  );
}
